import { ClientRegistry } from '../components';
import { FOOTSTEP_PARTICLE_CONFIG } from '../data/FootstepParticleConfig';
import {
  ClientFilteredComponentSystem,
  FilteredClientComponentSystemUpdateEntityArgs,
} from './ClientBase';

export class FootstepParticleEmitSystem extends ClientFilteredComponentSystem {
  public componentClasses: (keyof ClientRegistry)[] = [
    'Animation',
    'SpritesheetSprite',
    'VisualPosition',
    'Transform',
    'IsMoving',
  ];

  private lastEmittedFrame = new Map<number, number>();

  updateEntity({ world, entityId }: FilteredClientComponentSystemUpdateEntityArgs): void {
    const spritesheet = world.getComponent(entityId, 'SpritesheetSprite')!;
    const visualPosition = world.getComponent(entityId, 'VisualPosition')!;
    const transform = world.getComponent(entityId, 'Transform')!;

    // Ensure we have the current frame index from the sprite component
    const frameIndex = spritesheet.frameIndex;

    const lastEmitted = this.lastEmittedFrame.get(entityId);

    // Only attempt emission if the frame has changed since we last emitted
    if (lastEmitted !== frameIndex) {
      if (lastEmitted === undefined) {
        world.registerOnEntityDestroyed(entityId, (id) => {
          this.lastEmittedFrame.delete(id);
        });
      }

      const point = this.clientServices.spritesheet.getPointForFrame(
        spritesheet.spritesheetId,
        'FootstepParticlePoint',
        frameIndex,
      );

      if (point) {
        // Calculate emission position relative to the entity's visual anchor

        const size = this.clientServices.spritesheet.getFrameSize(spritesheet.spritesheetId);

        if (size) {
          // Get anchor from Sprite component if available, otherwise default to bottom-center
          const sprite = world.getComponent(entityId, 'Sprite');
          const anchorX = sprite ? sprite.anchorX : 0.5;
          const anchorY = sprite ? sprite.anchorY : 1.0;

          const originX = visualPosition.x - size.width * anchorX;
          const originY = visualPosition.y - size.height * anchorY;

          const emitX = originX + point.x;
          const emitY = originY + point.y;

          const terrain = this.clientServices.common.terrain.getTerrainAt(
            this.clientServices.chunkData,
            transform.sceneId,
            emitX,
            emitY,
          );

          if (terrain) {
            const config = FOOTSTEP_PARTICLE_CONFIG[terrain];
            if (config) {
              this.clientServices.footstepParticle.emit(emitX, emitY, config);
            }
          }
        }
      }

      // Update last emitted frame to prevent continuous emission on the same frame
      this.lastEmittedFrame.set(entityId, frameIndex);
    }
  }
}
